import { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNoteCategoryChoices } from "./useNoteCategoryChoices";
import { useNoteTitleChoices } from "./useNoteTitleChoices";
import { FOLLOWUP_SERVICE, REPORTS_SERVICE } from "../../../utils/constants";
import { useUser } from "../../../api/useAuth";
import { RecordContext } from "../../../components/record/Record";
import { TreatmentSession } from "../types";
import { getAllowedCategories } from "./caseManagementFeature";
import { getAvailableSessions } from "./NoteInputs";

const GERAL_CHOICE = { value: "geral", text: "Geral" };

const TITLES_BY_CATEGORY: Record<string, Array<string>> = {
	customer_support: ["notes", "comunication"],
	client_owner: ["notes", "comunication", "follow_up_sessions", "report_meeting_mid"],
};

const TITLE_SERVICE_MAP: Record<string, number> = {
	follow_up_sessions: FOLLOWUP_SERVICE,
	report_meeting_mid: REPORTS_SERVICE,
};

const TITLES_WITH_SESSION = Object.keys(TITLE_SERVICE_MAP);

export const useNoteCategories = () => {
	const [searchParams] = useSearchParams();
	const recordContext = useContext(RecordContext);
	const { data: user } = useUser();
	const allSessions : Array<TreatmentSession> = recordContext?.item?.sessions ?? [];

	const allCategoryChoices = useNoteCategoryChoices();
	const allTitleChoices = useNoteTitleChoices();

	const treatmentClientOwnerId = recordContext?.item?.client_owner?.user_id?.id;
	const allowedCategories = getAllowedCategories(user?.id, user?.role?.id, treatmentClientOwnerId);
	const showAdvancedCategories = allowedCategories.includes("geral");
	const directusChoices = allCategoryChoices.filter((choice) => allowedCategories.includes(choice.value));
	const categoryChoices = showAdvancedCategories ? [GERAL_CHOICE, ...directusChoices] : directusChoices;

	const initialCategory = searchParams.get("[category]") ?? (showAdvancedCategories ? "geral" : directusChoices[0]?.value);
	const [category, setCategory] = useState<string>(initialCategory);
	const [title, setTitle] = useState<string | null>(searchParams.get("[title]") ?? null);
	const [session, setSession] = useState<number | "" | null>(
		searchParams.get("[sessionId]") ? Number(searchParams.get("[sessionId]")) : null
	);

	const allowedTitleValues = TITLES_BY_CATEGORY[category] ?? [];
	const titleChoices = allTitleChoices.filter((choice) => allowedTitleValues.includes(choice.value));

	const titleHasSession = title !== null && TITLES_WITH_SESSION.includes(title);
	const titleServiceId = title ? TITLE_SERVICE_MAP[title] : null;

	const sortedSessions = [...allSessions].sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());
	const filteredSessions = titleServiceId
		? sortedSessions.filter((s) => s.service?.id === titleServiceId)
		: [];

	const onCategoryChange = (newCategory: string) => {
		setCategory(newCategory);
		setTitle(null);
		setSession(null);
	};

	const onTitleChange = (newTitle: string) => {
		setTitle(newTitle);
		setSession(null);
	};

	const onSessionChange = (sessionId: number | null) => {
		if (sessionId === null) { setSession(null); return; }
		if (session === null) setSession(sessionId);
	};

	const onCreateNew = () => {
		setTitle(null);
		setSession(null);
	};

	const onItemLoaded = (item: any) => {
		setTitle(item?.title ?? null);
	};

	const isGeral = category === "geral";

	const categoryState = {
		choices: categoryChoices,
		value: category,
		onChange: onCategoryChange,
		isGeral,
		isViewOnly: isGeral,
	};

	const titleState = {
		choices: titleChoices,
		allChoices: allTitleChoices,
		value: title,
		onChange: onTitleChange,
		hasSession: titleHasSession,
	};

	const sessionState = {
		value: session,
		filteredSessions,
		getAvailableSessions: (notes: Array<any>, currentSessionId?: number | null) =>
			getAvailableSessions(filteredSessions, notes, currentSessionId),
		setSession,
	};

	const callbacks = {
		onCreateNew,
		onItemLoaded,
		onSessionChange,
	};

	return { categoryState, titleState, sessionState, callbacks };
};
